import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import {
  buildRequestHeaders,
  logRequestHeaders,
  needsAuthentication,
} from '@/utils/request';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
/**
 * 与后端约定的统一响应数据格式
 *
 * @description 后端统一响应格式：{code, success, message, data, request_id, errors, debug}
 */
interface ResponseStructure {
  /** HTTP状态码 */
  code: number;
  /** 操作是否成功 */
  success: boolean;
  /** 响应消息 */
  message: string;
  /** 响应数据 */
  data: any;
  /** 请求追踪ID */
  request_id: string;
  /** 验证错误详情（可选） */
  errors?: Record<string, string[]>;
  /** 调试信息（仅开发环境，可选） */
  debug?: any;
  /** 错误显示类型（可选） */
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        code,
        message,
        data,
        request_id,
        errors,
        debug,
        showType,
      } = res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(message);
        error.name = 'BizError';
        error.info = {
          code,
          message,
          data,
          request_id,
          errors,
          debug,
          showType,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const {
            code,
            message: errorMessage,
            request_id,
            errors,
            debug,
          } = errorInfo;

          // 构建包含request_id的错误消息
          const errorMessageWithId = request_id
            ? `${errorMessage} (请求ID: ${request_id})`
            : errorMessage;

          // 在控制台输出详细错误信息用于调试
          console.error(`[API Error] ${errorMessage}`, {
            code,
            request_id,
            errors,
            debug,
          });

          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessageWithId);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessageWithId);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessageWithId,
                message: `错误码: ${code}`,
              });
              break;
            case ErrorShowType.REDIRECT:
              // 重定向错误处理：根据具体业务需求实现
              // 例如：history.push('/login') 或 window.location.href = '/error'
              break;
            default:
              message.error(errorMessageWithId);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行统一的请求头处理
      const url = config?.url || '';

      // 构建统一的请求头
      const unifiedHeaders = buildRequestHeaders(config.headers);

      // 检查是否需要认证（对于公开接口，移除认证头）
      if (!needsAuthentication(url)) {
        delete unifiedHeaders.Authorization;
      }

      // 在开发环境下记录请求头信息
      logRequestHeaders(url, unifiedHeaders);

      return {
        ...config,
        headers: unifiedHeaders,
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { success, message, request_id } =
        response as unknown as ResponseStructure;

      if (success === false) {
        // 在控制台输出调试信息
        console.warn(`[API Warning] ${message} (请求ID: ${request_id})`);
      }
      return response;
    },
  ],
};
